import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Upload, 
  FileText, 
  Image, 
  File,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Debtor, Document } from '../types';

interface DocumentsPageProps {
  debtor: Debtor;
}

interface UploadProgress {
  step: string;
  progress: number;
}

const DOCUMENT_TYPES = [
  { value: 'income_statement', label: 'Income Statement' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'employment_letter', label: 'Employment Letter' },
  { value: 'identity_document', label: 'Identity Document' },
  { value: 'proof_of_address', label: 'Proof of Address' },
  { value: 'hardship_declaration', label: 'Hardship Declaration' },
  { value: 'payment_proof', label: 'Payment Proof' },
  { value: 'medical_certificate', label: 'Medical Certificate' },
  { value: 'unemployment_certificate', label: 'Unemployment Certificate' },
  { value: 'other', label: 'Other Document' },
];

// Classify document type based on filename (local fallback)
function classifyByFilename(fileName: string): { type: string; confidence: number } {
  const lowerName = fileName.toLowerCase();
  
  const patterns: Array<{ keywords: string[]; type: string }> = [
    { keywords: ['income', 'salary', 'payslip', 'pay stub', 'nomina', 'sueldo'], type: 'income_statement' },
    { keywords: ['bank', 'statement', 'account', 'extracto', 'cuenta'], type: 'bank_statement' },
    { keywords: ['employment', 'employer', 'trabajo', 'empleo', 'contract'], type: 'employment_letter' },
    { keywords: ['passport', 'dni', 'id', 'identity', 'cedula', 'license'], type: 'identity_document' },
    { keywords: ['address', 'utility', 'factura', 'domicilio', 'proof', 'residence'], type: 'proof_of_address' },
    { keywords: ['hardship', 'dificultad', 'declaration', 'carta'], type: 'hardship_declaration' },
    { keywords: ['payment', 'receipt', 'pago', 'recibo', 'transfer'], type: 'payment_proof' },
    { keywords: ['medical', 'doctor', 'hospital', 'medico', 'health'], type: 'medical_certificate' },
    { keywords: ['unemployment', 'desempleo', 'paro', 'jobless'], type: 'unemployment_certificate' },
  ];

  for (const pattern of patterns) {
    if (pattern.keywords.some(kw => lowerName.includes(kw))) {
      return { type: pattern.type, confidence: 0.75 };
    }
  }

  return { type: 'other', confidence: 0.5 };
}

export default function DocumentsPage({ debtor }: DocumentsPageProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [aiClassification, setAiClassification] = useState<{ type: string; confidence: number } | null>(null);
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadDocuments();
  }, [debtor.debtor_id]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await serenityClient.queryData<Document>(
        `SELECT * FROM Documents WHERE debtor_id = '${debtor.debtor_id}' ORDER BY uploaded_at DESC`
      );
      setDocuments(result);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      
      // Auto-classify by filename
      const classification = classifyByFilename(file.name);
      setAiClassification(classification);
      
      // Auto-select document type if confidence is good
      if (classification.confidence >= 0.7 && classification.type !== 'other') {
        setDocumentType(classification.type);
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;
    
    // Use AI-suggested type if user hasn't selected one
    const finalDocType = documentType || aiClassification?.type || 'other';

    setIsUploading(true);
    setUploadSuccess(false);
    setUploadProgress({ step: 'Preparing document...', progress: 10 });

    try {
      // Step 1: Upload to Volatile Knowledge for AI processing
      setUploadProgress({ step: 'Uploading to secure storage...', progress: 20 });
      
      let volatileKnowledgeId: string | undefined;
      try {
        const vkResult = await serenityClient.uploadVolatileKnowledge(selectedFile);
        volatileKnowledgeId = vkResult.id;
        setUploadProgress({ step: 'Document uploaded, analyzing...', progress: 40 });
      } catch (vkError) {
        console.warn('Volatile Knowledge upload failed, continuing without AI analysis:', vkError);
        setUploadProgress({ step: 'Storing document...', progress: 40 });
      }

      // Step 2: Create document record
      const documentId = `DOC-${Date.now()}`;
      setUploadProgress({ step: 'AI analyzing document...', progress: 50 });
      
      // Step 3: Try AI classification if we have volatile knowledge
      let aiResult = aiClassification;
      if (volatileKnowledgeId) {
        try {
          const classificationResult = await serenityClient.classifyDocument(
            debtor.debtor_id,
            documentId,
            finalDocType,
            volatileKnowledgeId
          );
          aiResult = {
            type: classificationResult.documentType,
            confidence: classificationResult.confidence,
          };
          setUploadProgress({ step: `AI classified as "${classificationResult.documentType}"`, progress: 70 });
        } catch (classifyError) {
          console.warn('AI classification failed, using filename-based classification:', classifyError);
        }
      }

      // Step 4: Store document metadata
      setUploadProgress({ step: 'Saving document metadata...', progress: 80 });
      
      // Convert file to base64
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const newDocument: Partial<Document> = {
        document_id: documentId,
        debtor_id: debtor.debtor_id,
        document_type: aiResult?.type || finalDocType,
        document_name: selectedFile.name,
        file_type: selectedFile.type,
        file_size: selectedFile.size,
        description: description || undefined,
        upload_source: 'debtor_portal',
        status: 'processing',
        ai_confidence: aiResult?.confidence,
        volatile_knowledge_id: volatileKnowledgeId,
        uploaded_at: new Date().toISOString(),
      };

      await serenityClient.insertData('Documents', newDocument);

      // Step 5: Store file content
      setUploadProgress({ step: 'Storing file content...', progress: 90 });
      
      await serenityClient.insertData('FileContents', {
        document_id: documentId,
        content_base64: base64Content,
        mime_type: selectedFile.type,
        created_at: new Date().toISOString(),
      });

      // Reset form
      setSelectedFile(null);
      setDocumentType('');
      setDescription('');
      setAiClassification(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      setUploadProgress({ step: '✓ Document uploaded successfully!', progress: 100 });
      setUploadSuccess(true);
      
      setTimeout(() => {
        setUploadSuccess(false);
        setUploadProgress(null);
      }, 3000);
      
      // Reload documents
      loadDocuments();
    } catch (error) {
      console.error('Failed to upload document:', error);
      setUploadProgress(null);
      alert('Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <Image className="w-5 h-5 text-purple-600" />;
    }
    if (fileType === 'application/pdf') {
      return <FileText className="w-5 h-5 text-red-600" />;
    }
    return <File className="w-5 h-5 text-blue-600" />;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending_review':
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'pending_review':
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to="/dashboard"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Documents</h1>
                <p className="text-xs text-gray-500">Upload and manage your documents</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Document</h2>
              
              {uploadSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm">Document uploaded successfully!</span>
                </div>
              )}
              
              {/* Upload Progress */}
              {uploadProgress && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    {uploadProgress.progress < 100 ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    <span className="text-sm">{uploadProgress.step}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress.progress}%` }}
                    />
                  </div>
                </div>
              )}

              <form onSubmit={handleUpload} className="space-y-4">
                {/* File Drop Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    selectedFile 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    className="hidden"
                  />
                  
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      {getFileIcon(selectedFile.type)}
                      <div className="text-left">
                        <p className="font-medium text-gray-900 truncate max-w-[200px]">
                          {selectedFile.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className="p-1 hover:bg-red-100 rounded text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        PDF, JPG, PNG, DOC up to 10MB
                      </p>
                    </>
                  )}
                </div>
                
                {/* AI Classification Suggestion */}
                {aiClassification && selectedFile && (
                  <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-2 text-purple-700">
                      <Sparkles className="w-4 h-4" />
                      <span className="text-sm font-medium">AI Suggestion</span>
                    </div>
                    <p className="text-sm text-purple-600 mt-1">
                      Detected as <strong>{DOCUMENT_TYPES.find(t => t.value === aiClassification.type)?.label || aiClassification.type}</strong>
                      <span className="text-purple-400 ml-1">
                        ({Math.round(aiClassification.confidence * 100)}% confidence)
                      </span>
                    </p>
                  </div>
                )}

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Type {!aiClassification && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    required={!aiClassification}
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select type...</option>
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Add any notes about this document..."
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!selectedFile || !documentType || isUploading}
                  className="w-full py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Upload Document
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Documents List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Your Documents</h2>
              </div>

              {isLoading ? (
                <div className="p-8 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : documents.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No documents uploaded yet.</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Upload documents to support your case or hardship request.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {getFileIcon(doc.file_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-900 truncate">
                              {doc.document_name || doc.file_name}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(doc.status || 'pending')}`}>
                              {getStatusLabel(doc.status || 'pending')}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>{DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}</span>
                            <span>•</span>
                            <span>{formatFileSize(doc.file_size || doc.file_size_bytes || 0)}</span>
                            <span>•</span>
                            <span>{formatDate(doc.uploaded_at || doc.upload_date)}</span>
                          </div>
                          {doc.description && (
                            <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                          )}
                          {doc.reviewer_notes && (
                            <p className="text-sm text-orange-600 mt-1 bg-orange-50 p-2 rounded">
                              <strong>Reviewer:</strong> {doc.reviewer_notes}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getStatusIcon(doc.status || 'pending')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Card */}
            <div className="mt-6 bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-2">Why upload documents?</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Support a hardship or exception request</li>
                <li>• Verify your identity or employment</li>
                <li>• Provide proof of income or expenses</li>
                <li>• Speed up the review process</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
